// ---------------------------------------------------------------------------
// Applying a completed AEMET/ROCIO fetch's result to a project — split out
// of aemetJobStore.ts (see project_evalhid_code_audit.md's ongoing "god
// module" breakup). Pure functions, no dependency on the store's own state
// or job types.
// ---------------------------------------------------------------------------
import { enumerateWindows, expandSourceRanges, sourceRanges, type DateRange } from "./aemet.ts";
import {
  extendSeriesRange,
  insertColumnInSeries,
  insertColumnsInSeries,
  type ProjectSeries,
} from "../dailySeries.ts";
import { updateSubBasinColumn, updateSubBasinColumns } from "../projectEntities.ts";
import type { AemetProvenance } from "../storage.ts";
import type { EvalHidProject, SubBasin } from "../types.ts";

/**
 * Applies the 3 built series (precipitation/temperature/ETP) to the project,
 * reusing the exact same mechanism as "Paste series"
 * (insertColumnInSeries/updateSubBasinColumn, dailySeries.ts/projectEntities.ts) —
 * not a parallel insertion path. Can throw (dates not matching an existing
 * series of that SAME type) — the caller decides what to do with that.
 */
export function applyToProject(
  project: EvalHidProject,
  series: ProjectSeries,
  subBasin: SubBasin,
  dates: string[],
  precipValues: number[],
  tempValues: number[],
  etoValues: number[]
): { project: EvalHidProject; series: ProjectSeries } {
  let p = project;
  let s = series;

  const precip = insertColumnInSeries(s.precipitation, subBasin.description, {
    dates,
    values: precipValues,
  });
  p = updateSubBasinColumn(p, subBasin.subBasinCode, "precipCol", precip.index);
  s = { ...s, precipitation: precip.series };

  const temp = insertColumnInSeries(s.temperature, subBasin.description, {
    dates,
    values: tempValues,
  });
  p = updateSubBasinColumn(p, subBasin.subBasinCode, "tempCol", temp.index);
  s = { ...s, temperature: temp.series };

  const etp = insertColumnInSeries(s.etp, subBasin.description, { dates, values: etoValues });
  p = updateSubBasinColumn(p, subBasin.subBasinCode, "etpCol", etp.index);
  s = { ...s, etp: etp.series };

  return { project: p, series: s };
}

/** One sub-basin's worth of input to `applyBatchToProject` — same shape `applyToProject` takes per-call, grouped so a whole flushed batch (aemetJobStore.ts's `flushApplies`) can be applied in one pass. */
export interface BatchApplyEntry {
  subBasin: SubBasin;
  precipValues: number[];
  tempValues: number[];
  etoValues: number[];
}

/**
 * Batched form of `applyToProject` — applies MANY sub-basins' fetched series
 * in one pass over the project instead of one pass per sub-basin. See
 * `insertColumnsInSeries` (dailySeries.ts) and `updateSubBasinColumns`
 * (subBasinEntities.ts) for the real incident this fixes (2026-08-25, a
 * large MITECO+ROCIO import turning main-thread-blocking O(N²) into O(N)).
 * Every entry MUST share the same `dates` — true by construction for a
 * batch built from jobs in the same import, which all target the same
 * forced range (`rangeInfoForJob`); the caller is expected to only ever
 * group entries this way (aemetJobStore.ts's `flushApplies` does, splitting
 * by a `dates` fingerprint first).
 */
export function applyBatchToProject(
  project: EvalHidProject,
  series: ProjectSeries,
  dates: string[],
  entries: BatchApplyEntry[]
): { project: EvalHidProject; series: ProjectSeries } {
  if (entries.length === 0) return { project, series };

  const precip = insertColumnsInSeries(
    series.precipitation,
    dates,
    entries.map((e) => ({ columnName: e.subBasin.description, values: e.precipValues }))
  );
  const temp = insertColumnsInSeries(
    series.temperature,
    dates,
    entries.map((e) => ({ columnName: e.subBasin.description, values: e.tempValues }))
  );
  const etp = insertColumnsInSeries(
    series.etp,
    dates,
    entries.map((e) => ({ columnName: e.subBasin.description, values: e.etoValues }))
  );

  const updates = new Map<number, Partial<SubBasin>>();
  for (const e of entries) {
    updates.set(e.subBasin.subBasinCode, {
      precipCol: precip.indices.get(e.subBasin.description),
      tempCol: temp.indices.get(e.subBasin.description),
      etpCol: etp.indices.get(e.subBasin.description),
    });
  }

  return {
    project: updateSubBasinColumns(project, updates),
    series: { ...series, precipitation: precip.series, temperature: temp.series, etp: etp.series },
  };
}

/** `applyToProject`'s extend-mode counterpart — `extendSeriesRange` (dailySeries.ts) instead of `insertColumnInSeries`, so it grows the shared date axis instead of requiring `dates` to already match it. Never throws (unlike `applyToProject`), so no `t` needed. */
export function applyExtendToProject(
  project: EvalHidProject,
  series: ProjectSeries,
  subBasin: SubBasin,
  newDates: string[],
  precipValues: number[],
  tempValues: number[],
  etoValues: number[]
): { project: EvalHidProject; series: ProjectSeries } {
  let p = project;
  let s = series;

  const precip = extendSeriesRange(s.precipitation, subBasin.description, newDates, precipValues);
  p = updateSubBasinColumn(p, subBasin.subBasinCode, "precipCol", precip.index);
  s = { ...s, precipitation: precip.series };

  const temp = extendSeriesRange(s.temperature, subBasin.description, newDates, tempValues);
  p = updateSubBasinColumn(p, subBasin.subBasinCode, "tempCol", temp.index);
  s = { ...s, temperature: temp.series };

  const etp = extendSeriesRange(s.etp, subBasin.description, newDates, etoValues);
  p = updateSubBasinColumn(p, subBasin.subBasinCode, "etpCol", etp.index);
  s = { ...s, etp: etp.series };

  return { project: p, series: s };
}

/**
 * Both sides are already the compact "contiguous runs" shape
 * (`AemetProvenance.sourceByDate`'s own comment, storage.ts) — expands each
 * back to a per-day map (fresh always wins on a day both cover, same as the
 * old `new Map([...previous, ...fresh])`), then re-groups the result. Only
 * ever called for ONE sub-basin being extended/retried at a time, never for
 * a whole batch — the per-day expansion this needs is exactly the cost the
 * stored shape exists to avoid paying for every sub-basin on every save.
 */
function mergeSourceByDate(
  previous: Map<string | null, DateRange[]>,
  fresh: Map<string | null, DateRange[]>,
  unionRange: DateRange
): Map<string | null, DateRange[]> {
  const merged = new Map([...expandSourceRanges(previous), ...expandSourceRanges(fresh)]);
  const dates = enumerateWindows(unionRange, 1).map((w) => w.start);
  return sourceRanges(merged, dates);
}

/**
 * Folds a fresh AEMET fetch's provenance into whatever this sub-basin
 * already had — extend mode only (see `AemetJob.extendRange`'s own
 * comment, aemetJobStore.ts): a normal fetch always covers the SAME forced
 * range as any earlier one, so overwriting with the fresh provenance is
 * already equivalent to merging, and stays that way (no `previous` to fold
 * into the first time a sub-basin is ever fetched, extend or not). Extends
 * `dateRange` to the union of both, keeps every station either fetch ever
 * used, and concatenates `failedWindows` — `AemetProvenanceDialog.tsx`
 * needs no changes to render this correctly: it already regenerates its
 * date list from `dateRange` and reads `sourceByDate` generically.
 */
export function mergeProvenance(
  previous: AemetProvenance | undefined,
  fresh: AemetProvenance
): AemetProvenance {
  if (!previous) return fresh;
  const dateRange: DateRange = {
    start:
      previous.dateRange.start < fresh.dateRange.start
        ? previous.dateRange.start
        : fresh.dateRange.start,
    end:
      previous.dateRange.end > fresh.dateRange.end ? previous.dateRange.end : fresh.dateRange.end,
  };
  const knownCodes = new Set(previous.stationsUsed.map((s) => s.code));
  return {
    stationsUsed: [
      ...previous.stationsUsed,
      ...fresh.stationsUsed.filter((s) => !knownCodes.has(s.code)),
    ],
    method: fresh.method,
    sourceUpdatedAt: fresh.sourceUpdatedAt,
    fetchedAt: fresh.fetchedAt,
    dateRange,
    sourceByDate: {
      precipitation: mergeSourceByDate(
        previous.sourceByDate.precipitation,
        fresh.sourceByDate.precipitation,
        dateRange
      ),
      temperature: mergeSourceByDate(
        previous.sourceByDate.temperature,
        fresh.sourceByDate.temperature,
        dateRange
      ),
      etp: mergeSourceByDate(previous.sourceByDate.etp, fresh.sourceByDate.etp, dateRange),
    },
    failedWindows: [...previous.failedWindows, ...fresh.failedWindows],
  };
}
